import type { GemUniformName, MetalParamName } from "../../../shared/api-contract";
import type { RowDef, TabId } from "../inspector/schema";
import { TAB_SECTIONS } from "../sections";
import { SPARKLE_SLIDERS } from "../sections/custom/MaterialExtensionsControl";

/**
 * Where each look field (src/shared/lookFields.ts) is WRITTEN.
 *
 * Two kinds of target, and the difference is the whole design:
 *
 *   row       — a live Advanced inspector row, addressed `tab.section.row`.
 *               Basic mode writes through that row's own `binding`, via the
 *               same `store.mutate(binding.domain, binding.set(v, ctx))` the
 *               slider itself calls. A Basic press is therefore indistinguishable
 *               from dragging the slider, and the slider moves.
 *
 *   gem/metal — a per-part material value. The `material` slice holds ONLY the
 *               selected part, so a row binding would change one stone. These
 *               call the same RPC the row binding calls, once per part of that
 *               kind, and the engine then refreshes the `material` slice so the
 *               selected part's Advanced slider shows the new value. `row` still
 *               names the Advanced row, for "See in Advanced" and for the
 *               bounds check.
 *
 *   metalExt  — a per-part metal shading EXTENSION (the glitter flakes), written
 *               with `setGroupMaterialExtensions` once per metal part and read
 *               back with `getGroupMaterialExtensions`. Advanced shows these in
 *               a custom section with no binding, so `row` names that section's
 *               row for "See in Advanced" only, and the slider range lives in
 *               `METAL_EXT_SLIDERS` (the Advanced sliders' own numbers).
 */
export type MetalExtField = "sparkleEnabled" | "sparkleBumpScale" | "sparkleFlakeScale" | "sparkleFlakeRadius";

export type LookTarget =
  | { kind: "row"; row: string }
  | { kind: "gem"; uniform: GemUniformName; row: string }
  | { kind: "gemColor"; row: string }
  | { kind: "metal"; param: MetalParamName; row: string }
  | { kind: "metalColor"; row: string }
  | { kind: "metalExt"; field: MetalExtField; row: string };

/** The Advanced Sparkle Bump sliders, for the numeric `metalExt` fields. */
export const METAL_EXT_SLIDERS: Partial<Record<MetalExtField, { min: number; max: number; step: number }>> = SPARKLE_SLIDERS;

const GEM = "materials.diamondRayTracing";
const METAL = "materials.material";
const METAL_EXT = "materials.materialExtensions.ext";
const PROBE = "effects.reflectionProbe";

export const LOOK_TARGETS: Record<string, LookTarget> = {
  "gem.gammaFactor": { kind: "gem", uniform: "gammaFactor", row: `${GEM}.gammaFactor` },
  "gem.bounces": { kind: "gem", uniform: "bounces", row: `${GEM}.bounces` },
  "gem.sparkleBoost": { kind: "gem", uniform: "sparkleBoost", row: `${GEM}.sparkleBoost` },
  "gem.dispersion": { kind: "gem", uniform: "dispersion", row: `${GEM}.dispersion` },
  "gem.envMapIntensity": { kind: "gem", uniform: "envMapIntensity", row: `${GEM}.envMapIntensity` },
  "gem.internalEnvBoost": { kind: "gem", uniform: "internalEnvBoost", row: `${GEM}.internalEnvBoost` },
  "gem.surfaceReflBoost": { kind: "gem", uniform: "surfaceReflBoost", row: `${GEM}.surfaceReflBoost` },
  "gem.reflectivity": { kind: "gem", uniform: "reflectivity", row: `${GEM}.reflectivity` },
  "gem.absorptionFactor": { kind: "gem", uniform: "absorptionFactor", row: `${GEM}.absorptionFactor` },
  "gem.color": { kind: "gemColor", row: `${GEM}.color` },
  "gem.ior": { kind: "gem", uniform: "ior", row: `${GEM}.ior` },
  "gem.envRotation": { kind: "gem", uniform: "envRotation", row: `${GEM}.envRotation` },

  // The reflection probe is GLOBAL (one capture of the whole piece); its
  // asGemEnv row's binding also switches the probe on.
  "probe.enabled": { kind: "row", row: `${PROBE}.enabled` },
  "probe.gemReflect": { kind: "row", row: `${PROBE}.asGemEnv` },
  "probe.gemStrength": { kind: "row", row: `${PROBE}.gemProbeIntensity` },

  "metal.metalness": { kind: "metal", param: "metalness", row: `${METAL}.metalness` },
  "metal.roughness": { kind: "metal", param: "roughness", row: `${METAL}.roughness` },
  "metal.envMapIntensity": { kind: "metal", param: "envMapIntensity", row: `${METAL}.envMapIntensity` },
  "metal.specularIntensity": { kind: "metal", param: "specularIntensity", row: `${METAL}.specularIntensity` },
  "metal.color": { kind: "metalColor", row: `${METAL}.color` },
  "metal.clearcoat": { kind: "metal", param: "clearcoat", row: `${METAL}.clearcoat` },
  "metal.sparkle": { kind: "metalExt", field: "sparkleEnabled", row: METAL_EXT },
  "metal.sparkleBump": { kind: "metalExt", field: "sparkleBumpScale", row: METAL_EXT },
  "metal.sparkleSize": { kind: "metalExt", field: "sparkleFlakeRadius", row: METAL_EXT },
  "metal.sparkleScale": { kind: "metalExt", field: "sparkleFlakeScale", row: METAL_EXT },

  "ssr.enabled": { kind: "row", row: "effects.ssr.enabled" },
  "ssr.intensity": { kind: "row", row: "effects.ssr.intensity" },
  "ssr.wMetal": { kind: "row", row: "effects.ssr.wMetal" },
  "ssr.wGem": { kind: "row", row: "effects.ssr.wGem" },

  "shadow.enabled": { kind: "row", row: "ground.groundShadowBake.enable" },
  "shadow.darkness": { kind: "row", row: "ground.groundShadowBake.darkness" },
  "shadow.softness": { kind: "row", row: "ground.groundShadowBake.softness" },
  "shadow.blur": { kind: "row", row: "ground.groundShadowBake.blur" },
  "shadow.spread": { kind: "row", row: "ground.groundShadowBake.spread" },
  "shadow.falloff": { kind: "row", row: "ground.groundShadowBake.falloff" },
  "floor.reflection": { kind: "row", row: "ground.ground.reflection" },
  "floor.reflectionStrength": { kind: "row", row: "ground.ground.reflectionStrength" },
  "floor.reflectionBlur": { kind: "row", row: "ground.ground.reflectionBlur" },

  "light.exposure": { kind: "row", row: "effects.tonemapping.exposure" },
  "light.envIntensity": { kind: "row", row: "lighting.environment.envIntensity" },
  "light.contrast": { kind: "row", row: "effects.tonemapping.contrast" },
  "light.saturation": { kind: "row", row: "effects.tonemapping.saturation" },
  "bloom.enabled": { kind: "row", row: "effects.bloom.enabled" },
  "bloom.intensity": { kind: "row", row: "effects.bloom.intensity" },
  "bloom.threshold": { kind: "row", row: "effects.bloom.threshold" },
  "light.background": { kind: "row", row: "lighting.environment.background" },
};

export interface ResolvedRow {
  tab: TabId;
  section: string;
  row: RowDef;
}

/** Resolve `tab.section.row` against the live section registry, or null. */
export function resolveRow(path: string): ResolvedRow | null {
  const [tab, section, rowId] = path.split(".");
  if (!tab || !section || !rowId) return null;
  const sections = TAB_SECTIONS[tab as TabId];
  if (!sections) return null;
  const sec = sections.find((s) => s.id === section);
  const row = sec?.rows.find((r) => r.id === rowId);
  return row ? { tab: tab as TabId, section, row } : null;
}
